<template>
  <PageLoading :loading="loading">
    <!-- 错误页面 -->
    <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

    <!-- 正常页面 -->
    <div v-else class="card-detail-page">
      <!-- 消费记录按钮 -->
      <div class="header-actions">
        <van-button class="record-btn" @click="viewRecord" :loading="recordLoading">
          <van-icon name="bars" />
          {{ recordLoading ? '加载中...' : '消费记录' }}
        </van-button>
      </div>

      <!-- 卡片详情 -->
      <div class="detail-section">
        <div class="detail-item" v-for="item in detailItems" :key="item.key">
          <span class="detail-label">{{ item.label }}</span>
          <span class="detail-value" :class="item.highlight ? 'highlight' : ''">{{ item.value }}</span>
        </div>
      </div>

      <!-- 消费记录弹窗 -->
      <van-popup v-model:show="showRecordPopup" round closeable position="center"
        :style="{ width: '90%', padding: '0', maxHeight: '80%' }">
        <div class="record-popup-content">
          <div class="record-title">消费记录</div>
          <div class="card-info">
            <van-row class="card-info-item">
              <van-col span="8" class="card-info-label">卡名称：</van-col>
              <van-col span="16" class="card-info-value">{{ cardRecord.name }}</van-col>
            </van-row>
            <van-row class="card-info-item">
              <van-col span="8" class="card-info-label">卡号：</van-col>
              <van-col span="16" class="card-info-value">{{ cardRecord.number }}</van-col>
            </van-row>
          </div>
          <van-cell-group class="record-list">
            <!-- 空状态 -->
            <div v-if="cardRecord.records.length === 0" class="empty-state">
              <van-empty description="暂无消费记录" />
            </div>
            <!-- 消费记录列表 -->
            <van-cell v-else v-for="item in cardRecord.records" :key="item.id" class="record-item">
              <template #title>
                <div class="record-content">
                  <div class="record-left">
                    <div class="record-id">{{ item.use_no }}</div>
                    <div class="record-time">{{ item.use_time }}</div>
                  </div>
                  <div class="record-amount" v-if="item.card_type == '金额卡'">{{ item.use_amount }}</div>
                  <div class="record-amount" v-if="item.card_type == '瓶数卡'">{{ item.use_number }}</div>
                </div>
              </template>
            </van-cell>
          </van-cell-group>
          <div class="record-footer">
            <van-button type="default" class="close-btn" @click="showRecordPopup = false">
              关闭
            </van-button>
          </div>
        </div>
      </van-popup>
    </div>
  </PageLoading>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { post } from '@/utils/request'
import { WECHAT_APPID } from '../../../config/env'
import { Dialog } from 'vant'

// 加载状态
const loading = ref(true)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 控制弹窗显示
const showRecordPopup = ref(false)

// 消费记录加载状态
const recordLoading = ref(false)

// 卡片详情数据
const detailItems = ref([])

// 消费记录数据
const cardRecord = ref({
  card_id: '',
  name: '',
  number: '',
  records: []
})

// 处理错误
const handleError = (title, message) => {
  hasError.value = true
  errorTitle.value = title
  errorMessage.value = message
  loading.value = false
}

onMounted(() => {
  setWxTitle('卡片详情')
  initPage()
})

// 页面初始化
const initPage = async () => {
  try {
    // 获取URL参数中的卡片ID
    const urlParams = new URLSearchParams(window.location.search)
    const cardId = urlParams.get('card_id')

    if (!cardId) {
      handleError('参数错误', '卡片ID缺失')
      return
    }

    // 调用接口获取卡片详情
    const res = await post('/api/select/community/card/info', {
      partner_id: parseInt(localStorage.getItem('userId')),
      card_id: cardId,
      app_id: WECHAT_APPID,
    })

    if (res.result && res.result.state === 'ok') {
      const cardData = res.result.card_info_arr[0]
      cardRecord.value.name = cardData.card_name
      cardRecord.value.number = cardData.card_no
      cardRecord.value.card_id = cardData.card_id

      // 更新卡片详情数据
      detailItems.value = [
        {
          key: 'name',
          label: '卡名称',
          value: cardData.card_name || '',
          highlight: true
        },
        {
          key: 'number',
          label: '卡号',
          value: cardData.card_no || '',
          highlight: true
        },
        {
          key: 'balance',
          label: '剩余金额',
          value: cardData.card_type === '金额卡' ? `¥${cardData.card_balance || '0.00'}` : '--',
          highlight: false
        },
        {
          key: 'bottles',
          label: '剩余瓶数',
          value: cardData.card_type === '瓶数卡' ? `${cardData.surplus_number || 0}瓶` : '--',
          highlight: false
        },
        ...(cardData.card_type !== '金额卡' ? [{
          key: 'discount',
          label: '单瓶最高抵扣金额',
          value: `¥${cardData.bottle_price || '0.00'}`,
          highlight: false
        }] : []),
        {
          key: 'usedAmount',
          label: '已消费金额',
          value: `¥${cardData.used_amount !== undefined && cardData.used_amount !== null ? cardData.used_amount.toFixed(2) : '0.00'}`,
          highlight: false
        },
        {
          key: 'bindDate',
          label: '绑定日期',
          value: cardData.bind_date || '',
          highlight: false
        },
        {
          key: 'expireDate',
          label: '到期日期',
          value: cardData.expiration_date || '',
          highlight: false
        },
        {
          key: 'company',
          label: '发行公司',
          value: cardData.company_name || '',
          highlight: false
        },
        {
          key: 'description',
          label: '卡种说明',
          value: cardData.remark || '',
          highlight: false
        }
      ]
    } else {
      handleError('加载失败', res.result?.message || '接口异常')
    }
  } catch (error) {
    console.error('页面初始化失败:', error)
    handleError('加载失败', '页面加载失败，请重试')
  } finally {
    loading.value = false
  }
}

// 查看消费记录
const viewRecord = async () => {
  recordLoading.value = true
  
  try {
    const res = await post('/api/select/community/card/record', {
      card_id: cardRecord.value.card_id,
    })
    if (res.result && res.result.state === 'ok') {
      showRecordPopup.value = true
      cardRecord.value.records = res.result.card_record_arr || []
    } else {
      Dialog.alert({
        title: '查看消费记录失败',
        message: res.result?.message || '接口异常'
      })
    }
  } catch (error) {
    console.error('查看消费记录失败:', error)
    Dialog.alert({
      title: '查看消费记录失败',
      message: '接口异常'
    })
  } finally {
    recordLoading.value = false
  }
}
</script>

<style scoped>
.card-detail-page {
  min-height: calc(100vh - 32px);
  background: #fff;
  padding: 16px 32px;
}

.header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
}

.record-btn {
  background: #fff !important;
  color: var(--van-primary-color) !important;
  border: 1px solid var(--van-primary-color) !important;
  border-radius: 20px !important;
  padding: 8px 16px !important;
  font-size: 14px !important;
  height: 36px !important;
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
}

.detail-section {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 14px;
  color: #666;
  flex-shrink: 0;
}

.detail-value {
  font-size: 14px;
  color: #333;
  text-align: right;
  flex: 1;
  margin-left: 16px;
}

.detail-value.highlight {
  color: var(--van-primary-color);
  font-weight: 500;
}

/* 消费记录弹窗样式 */
.record-popup-content {
  padding: 24px 20px 16px 20px;
}

.record-title {
  text-align: center;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 18px;
}

.card-info {
  margin-bottom: 10px;
  /* padding: 0 20px; */
}

.card-info-item {
  margin-bottom: 6px;
}

.card-info-label {
  color: var(--van-primary-color);
  font-weight: 600;
  text-align: right;
  padding-right: 8px;
}

.card-info-value {
  color: var(--van-primary-color);
  font-weight: 600;
}

.record-item {
  padding: 10px 0;
}

.record-item::after {
  left: 0;
  right: 0;
}

/* :deep(.record-popup-content) {
  height: 70vh;
  overflow: hidden;
  padding: 24px 0;
} */

/* .record-list {
  height: 68%;
  overflow: auto;
  padding: 0 20px;
} */

.record-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.record-left {
  flex: 1;
}

.record-id {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  margin-bottom: 4px;
}

.record-time {
  color: #bbb;
  font-size: 13px;
}

.record-amount {
  color: #333;
  font-size: 18px;
  font-weight: 600;
  text-align: right;
}

.record-footer {
  text-align: center;
  margin-top: 20px;
}

.close-btn {
  width: 120px;
  border-radius: 20px;
  background: #f5f5f5;
  border: none;
  color: #666;
}
</style>